import { useEffect } from "react";
import { useDispatch } from "react-redux";

import KeyboardArrowRightIcon from "@material-ui/icons/KeyboardArrowRight";
import { Button, Checkbox, Divider, InputNumber, Space } from "antd";
import {
  DEFAULT_NORMALIZE_BY_HIGHER_SCALER,
  DEFAULT_NORMALIZE_BY_LOWER_SCALER,
  DEFAULT_NORMALIZE_BY_THRESHOLD
} from "constants/chart.constants";
import { useChartsContext } from "contexts/ChartContext";
import { updateChartByPath } from "store/features";
import styled from "styled-components";

import useNormalizeFieldConversion from "hooks/useNormalizeFieldConversion";
import useUserUnit from "hooks/useUserUnit";

import { INormalizeBySetting } from "models";

import { LabeledSwitch } from "components/base";
import GroupBy from "components/groupBy";

import { useChartDependencies, useChartEntities } from "../../../../hooks/charts";
import useTypeWellSeries from "../../hooks/useTypeWellSeries";

const NormalizeBySettings = () => {
  const dispatch = useDispatch();
  const { id, entityKind } = useChartsContext();
  const unitType = useUserUnit();
  const { normalizeBy, typewells } = useChartEntities(id);
  const { checkedGlobalTypeWells, globalNormalizeBy, normalizeTypeWell } =
    useChartDependencies(entityKind);
  useTypeWellSeries(
    checkedGlobalTypeWells,
    normalizeBy?.active && normalizeBy?.properties
      ? normalizeBy.properties
      : globalNormalizeBy,
    normalizeBy?.active,
    normalizeTypeWell,
    typewells?.active
  );

  const normalizationSettings = normalizeBy.properties;

  const {
    displayName,
    field,
    per,
    unit,
    defaultUnit,
    useMultilinearNormalization,
    useNormalizeBy,
    threshold,
    higherScalar,
    units
  } = normalizationSettings;

  // Extract date range suffix before stripping the unit, so it isn't lost
  const dateRangeMatch = displayName.match(
    /\s*\(\d{4}-\d{2}-\d{2} to \d{4}-\d{2}-\d{2}\)\s*$/
  );
  const dateRangeSuffix = dateRangeMatch ? dateRangeMatch[0].trimStart() : "";
  const nameWithoutDateRange = dateRangeSuffix
    ? displayName.slice(0, displayName.lastIndexOf(dateRangeSuffix)).trim()
    : displayName;

  const { displayLabel, displayUnit } = useNormalizeFieldConversion({
    field,
    fallbackDisplay: nameWithoutDateRange.replace(/\s*\(.*?\)\s*$/g, "").trim(),
    fallbackUnit: unit,
    units,
    defaultUnit
  });

  const displayLabelWithUnit = displayUnit
    ? `${displayLabel} (${displayUnit})${dateRangeSuffix ? ` ${dateRangeSuffix}` : ""}`
    : `${displayLabel}${dateRangeSuffix ? ` ${dateRangeSuffix}` : ""}`;

  const groupByValue = {
    property: field,
    title: displayLabelWithUnit
  };

  useEffect(() => {
    if (units && units[unitType] && unit !== units[unitType]) {
      dispatch(
        updateChartByPath({
          id,
          path: "features.normalization.properties.unit",
          value: units[unitType]
        })
      );
    }
  }, [id, unitType, units, unit, dispatch]);

  const handleNormalizationSettingsChange =
    (key: keyof INormalizeBySetting) => (value: string | number | boolean) => {
      dispatch(
        updateChartByPath({
          id,
          path: `features.normalization.properties.${key}`,
          value
        })
      );
    };

  const handleFieldBlur = (key: keyof INormalizeBySetting) => () => {
    const currentValue = normalizationSettings[key];
    if (typeof currentValue === "number" && currentValue <= 0) {
      dispatch(
        updateChartByPath({
          id,
          path: `features.normalization.properties.${key}`,
          value: 1 //prevents users from normalizing by 0.
        })
      );
    }
  };

  const handleToggle = (active: boolean) => {
    handleNormalizationSettingsChange("useNormalizeBy")(active);
    dispatch(
      updateChartByPath({
        id,
        path: "features.normalization.active",
        value: active
      })
    );
  };

  const handleFieldChange = (groupBy): void => {
    const { property, title, defaultUnit, normalizeByDefault, units } = groupBy;

    let convertedUnit = defaultUnit;
    if (units && units[unitType]) {
      convertedUnit = units[unitType];
    }

    const next: Partial<INormalizeBySetting> = {
      ...normalizationSettings,
      displayName: title,
      field: property,
      per: normalizeByDefault > 0 ? normalizeByDefault : 1,
      unit: convertedUnit,
      defaultUnit: defaultUnit, // Keep original unit for backend and eva-unit-core conversion
      units: units,
      useMultilinearNormalization: false,
      threshold: DEFAULT_NORMALIZE_BY_THRESHOLD,
      lowerScalar: DEFAULT_NORMALIZE_BY_LOWER_SCALER, //technically not able to edit this value anymore, leaving it as default
      higherScalar: DEFAULT_NORMALIZE_BY_HIGHER_SCALER
    };

    dispatch(
      updateChartByPath({
        id,
        path: "features.normalization.properties",
        value: next
      })
    );
  };

  return (
    <Wrapper>
      <StyledSpace direction="vertical">
        <LabeledSwitch
          testId="normalizeSwitch"
          switch={{
            isChecked: useNormalizeBy as boolean,
            onChange: handleToggle
          }}
          label={{
            value: "Normalize",
            isBold: true,
            isUppercase: true
          }}
        />
        {useNormalizeBy && (
          <>
            <StyledDivider />
            <Row>
              <LeftField>
                <Label>Normalize Field</Label>
                <GroupBy
                  value={groupByValue}
                  dataTypeFilters={["Number", "Integer"]}
                  editable={false}
                  placement="rightTop"
                  onChange={handleFieldChange}>
                  <StyledButton block>
                    {displayLabelWithUnit}
                    <KeyboardArrowRightIcon style={{ fontSize: 24 }} />
                  </StyledButton>
                </GroupBy>
              </LeftField>

              <RightField>
                <Label>Per{displayUnit && ` (${displayUnit})`}</Label>
                <StyledNumberInput
                  data-testid="normalizePerInput"
                  value={per}
                  min={0}
                  controls={false}
                  onChange={handleNormalizationSettingsChange("per")}
                  onBlur={handleFieldBlur("per")}
                />
              </RightField>
            </Row>
            <Row>
              <Checkbox
                data-testid="normalizeMultilinearCheckbox"
                checked={useMultilinearNormalization as boolean}
                onChange={(v) =>
                  handleNormalizationSettingsChange("useMultilinearNormalization")(
                    v.target.checked
                  )
                }>
                Multilinear Normalization
              </Checkbox>
            </Row>
            {useMultilinearNormalization && (
              <Row>
                <CenterField>
                  <Label>Cut Off Threshold{displayUnit && ` (${displayUnit})`}</Label>
                  <StyledNumberInput
                    data-testid="normalizeThresholdInput"
                    value={threshold}
                    controls={false}
                    min={0}
                    precision={0}
                    onChange={handleNormalizationSettingsChange("threshold")}
                    onBlur={handleFieldBlur("threshold")}
                  />
                </CenterField>
                <CenterField>
                  <Label>Beyond Threshold Scalar</Label>
                  <StyledNumberInput
                    data-testid="normalizeHigherScalarInput"
                    value={higherScalar}
                    controls={false}
                    min={0}
                    onChange={handleNormalizationSettingsChange("higherScalar")}
                    onBlur={handleFieldBlur("higherScalar")}
                  />
                </CenterField>
              </Row>
            )}
          </>
        )}
      </StyledSpace>
    </Wrapper>
  );
};

export default NormalizeBySettings;

const Row = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 5%;
  margin-bottom: 10px;
`;

const LeftField = styled.div`
  width: 62%;
`;

const RightField = styled.div`
  width: 33%;
`;

const Label = styled.span`
  white-space: break-spaces;
  text-overflow: ellipsis;
  overflow: hidden;
  display: block;
  margin-top: 0;
`;

const StyledButton = styled(Button)`
  width: 100%;
  display: inline-flex;
  justify-content: space-between;
  height: 30px;
  text-align: left;

  & > span {
    max-width: fit-content;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const StyledNumberInput = styled(InputNumber)`
  width: 100%;
  height: 30px;
`;

const Wrapper = styled.div`
  width: 380px;
  max-width: 380px;
  background: var(--normalize-section-bg, #ffffff);
  font-weight: 500;
  padding: 20px 20px;
  text-align: left;
`;

const StyledDivider = styled(Divider)`
  margin: 0;
`;

const StyledSpace = styled(Space)`
  width: 100%;
`;

const CenterField = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;
